// shared/assets.ts
// Asset library vocabulary shared by api/src/assets.ts (validation, the
// source of truth) and src/Library.tsx (pickers, filters) — kinds, image
// roles, description status, per-kind size caps, and the field limits for
// user-editable metadata.
// ─────────────────────────────────────────────────────────────────
/** What a file *is*. Purpose (background vs cover, etc.) is a role tag on image assets, not a kind. */
export const ASSET_KINDS = ['image', 'music', 'video'];
export function isAssetKind(v) {
    return typeof v === 'string' && ASSET_KINDS.includes(v);
}
const ASSET_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** Asset ids are lowercase UUIDs. */
export function isAssetId(v) {
    return typeof v === 'string' && ASSET_ID_RE.test(v);
}
/**
 * What an image can be used for in a video. An image can have several (a
 * cover can also work as a background). Suggested by the description job,
 * editable by the user; pickers filter by role but still allow any image.
 */
export const IMAGE_ROLES = ['background', 'cover', 'cover-3d', 'character', 'logo', 'badge', 'kindle-page'];
export function isImageRole(v) {
    return typeof v === 'string' && IMAGE_ROLES.includes(v);
}
/**
 * Lifecycle of an asset's AI description:
 * pending → running → done | failed. 'skipped' means no AI description is
 * coming: music (Claude can't listen to it), an account with AI off, or the
 * user chose "Write it myself" (descBy 'user'). A user edit sets 'done' with
 * descBy 'user', which the job never overwrites unless asked.
 *
 * While 'pending' or 'running' the AI-written fields (DESC_FIELDS) are
 * locked: the API refuses edits to them until the user takes the asset out
 * of the queue, so typing can't silently cancel a description.
 */
export const DESC_STATUSES = ['pending', 'running', 'done', 'failed', 'skipped'];
/**
 * Colours and text bands are measured by the worker for every image and
 * video, with or without an AI description (ring/worker/measure.py):
 * pending → done | failed. Null for music, which has nothing to measure.
 */
export const MEASURE_STATUSES = ['pending', 'done', 'failed'];
/** The user-editable fields the description job writes, locked while it's queued. */
export const DESC_FIELDS = ['descShort', 'descLong', 'mood'];
/** Statuses that mean the description is waiting for, or with, the worker. */
export const DESC_QUEUED_STATUSES = ['pending', 'running'];
export function isDescQueued(status) {
    return DESC_QUEUED_STATUSES.includes(status);
}
/** Kinds the description job can look at. */
export const DESCRIBABLE_KINDS = ['image', 'video'];
export function isDescribable(kind) {
    return DESCRIBABLE_KINDS.includes(kind);
}
/**
 * The words the description job picks a mood from (1–3, comma-separated),
 * so drafting can match a hook's tone consistently. Users can type anything.
 */
export const MOOD_WORDS = [
    'ominous', 'eerie', 'tense', 'epic', 'triumphant', 'adventurous', 'romantic',
    'wistful', 'melancholy', 'serene', 'cozy', 'hopeful', 'whimsical', 'mysterious',
];
/** make_video's zoom pin points, a 3×3 grid (FbAdPipeline/docs/make-video.md). */
export const ZOOM_TARGETS = [
    'upper-left', 'upper-center', 'upper-right',
    'middle-left', 'center', 'middle-right',
    'lower-left', 'lower-center', 'lower-right',
];
/** Text is centred and placed by height, so where it can go is a horizontal band of the 9:16 frame. */
export const TEXT_BANDS = ['upper', 'middle', 'lower'];
/** Upload caps per kind. Uploads go through the Functions host, so they must also stay under its request-size limit (~100 MB on SWA). */
export const MAX_UPLOAD_BYTES = {
    image: 25 * 1024 * 1024,
    music: 50 * 1024 * 1024,
    video: 90 * 1024 * 1024,
};
/** Assets one describe-assets job may carry (an upload batch is queued as one job). */
export const MAX_DESCRIBE_ASSETS = 100;
export const MAX_THUMB_BYTES = 1024 * 1024;
export const THUMB_MAX_DIMENSION = 480;
export const MAX_TAGS = 20;
export const MAX_TAG_LENGTH = 40;
export const MAX_BOOK_CODE_LENGTH = 20;
export const MAX_DESC_SHORT = 300;
export const MAX_DESC_LONG = 2000;
export const MAX_MOOD_LENGTH = 60;
export const MAX_FILENAME_LENGTH = 200;
